import { useState } from "react";
import { Drawer, Alert, Divider, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_FORM_WIDTH,
} from "../../../../components";
import {
  useTenantPackageQuery,
  useTenantPackageHistoryQuery,
  useTenantPackageChargesQuery,
  useAssignTenantPackageMutation,
  useUnassignTenantPackageMutation,
  useAddTenantPackageChargeMutation,
  useDeleteTenantPackageChargeMutation,
  useSystemPackagesQuery,
} from "../../../../Services";
import { PackageCurrentPlan } from "./PackageCurrentPlan";
import { PackageAssignForm } from "./PackageAssignForm";
import { PackageAdditionalCharges } from "./PackageAdditionalCharges";
import { PackageCostHistory } from "./PackageCostHistory";

interface Props {
  open: boolean;
  tenantId: string | undefined;
  onClose: () => void;
}

export const TenantPackageDrawer: React.FC<Props> = ({
  open,
  tenantId,
  onClose,
}) => {
  const [selectedPackage, setSelectedPackage] = useState<string | undefined>(
    undefined,
  );
  const [reassignNote, setReassignNote] = useState<string>("");
  const [chargeAmount, setChargeAmount] = useState<number | null>(null);
  const [chargeDesc, setChargeDesc] = useState<string>("");

  const { data: current, isLoading, refetch } = useTenantPackageQuery(tenantId);
  const { data: history = [], isLoading: historyLoading } =
    useTenantPackageHistoryQuery(tenantId);
  const { data: charges = [], isLoading: chargesLoading } =
    useTenantPackageChargesQuery(tenantId);
  const { data: packages = [], isLoading: packagesLoading } =
    useSystemPackagesQuery();

  const assignMutation = useAssignTenantPackageMutation();
  const unassignMutation = useUnassignTenantPackageMutation();
  const addChargeMutation = useAddTenantPackageChargeMutation();
  const deleteChargeMutation = useDeleteTenantPackageChargeMutation();

  const currentPackageId = current?.package_id;
  const base = Number(current?.amount) || 0;
  const additional = Number(current?.additional_amount) || 0;
  const total = Number(current?.total_amount) || base + additional;

  const handleAssign = async () => {
    if (!tenantId || !selectedPackage) return;
    try {
      const res = await assignMutation.mutateAsync({
        tenant_id: tenantId,
        package_id: selectedPackage,
        note: reassignNote,
      });
      if (res?.success) {
        message.success(res.message || "Package assigned.");
        setSelectedPackage(undefined);
        setReassignNote("");
        refetch();
      } else {
        throw new Error(res?.message || "Failed to assign package.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to assign package.");
    }
  };

  const handleUnassign = async () => {
    if (!tenantId) return;
    try {
      const res = await unassignMutation.mutateAsync(tenantId);
      if (res?.success) {
        message.success(res.message || "Package unassigned.");
        refetch();
      } else {
        throw new Error(res?.message || "Failed to unassign package.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to unassign package.");
    }
  };

  const handleAddCharge = async () => {
    if (!tenantId) return;
    if (chargeAmount === null || chargeAmount === 0) {
      message.warning("Enter a non-zero amount.");
      return;
    }
    if (!chargeDesc.trim()) {
      message.warning("Add a reason for this charge.");
      return;
    }
    try {
      const res = await addChargeMutation.mutateAsync({
        tenant_id: tenantId,
        amount: chargeAmount,
        description: chargeDesc.trim(),
      });
      if (res?.success) {
        message.success(res.message || "Charge added.");
        setChargeAmount(null);
        setChargeDesc("");
      } else {
        throw new Error(res?.message || "Failed to add charge.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to add charge.");
    }
  };

  const handleDeleteCharge = async (chargeId: string) => {
    if (!tenantId) return;
    try {
      const res = await deleteChargeMutation.mutateAsync({
        tenant_id: tenantId,
        charge_id: chargeId,
      });
      if (res?.success) {
        message.success(res.message || "Charge removed.");
      } else {
        throw new Error(res?.message || "Failed to remove charge.");
      }
    } catch (err: any) {
      message.error(err.message || "Failed to remove charge.");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      title={drawerTitle(
        "Package & Billing",
        "Assign a plan, bill extra features, and review the full cost history",
      )}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4">
        <PackageCurrentPlan
          isLoading={isLoading}
          current={current}
          currentPackageId={currentPackageId}
          base={base}
          additional={additional}
          total={total}
        />

        <PackageAssignForm
          currentPackageId={currentPackageId}
          packages={packages}
          packagesLoading={packagesLoading}
          selectedPackage={selectedPackage}
          onSelectPackage={setSelectedPackage}
          reassignNote={reassignNote}
          onReassignNoteChange={setReassignNote}
          onAssign={handleAssign}
          assignPending={assignMutation.isPending}
          onUnassign={handleUnassign}
          unassignPending={unassignMutation.isPending}
        />

        <PackageAdditionalCharges
          charges={charges}
          chargesLoading={chargesLoading}
          currentPackageId={currentPackageId}
          chargeAmount={chargeAmount}
          onChargeAmountChange={setChargeAmount}
          chargeDesc={chargeDesc}
          onChargeDescChange={setChargeDesc}
          onAddCharge={handleAddCharge}
          addChargePending={addChargeMutation.isPending}
          onDeleteCharge={handleDeleteCharge}
        />

        <Divider className="!my-1" />

        <PackageCostHistory
          history={history}
          historyLoading={historyLoading}
          onRefresh={refetch}
        />

        {!isLoading && !currentPackageId && (
          <Alert
            type="info"
            showIcon
            className="border-blue-100 bg-blue-50"
            message={
              <span className="text-xs text-blue-800 flex items-center gap-2">
                <CheckCircleOutlined />
                Features can still be enabled or disabled manually per tenant.
              </span>
            }
          />
        )}
      </div>
    </Drawer>
  );
};
